/**
 * Reusable inline form cells for the criteria table.
 * Shared between the "add new" row and the "edit existing" row
 * to eliminate duplication.
 */
import { Spinner } from "@chakra-ui/react";
import { FiCheck, FiX } from "react-icons/fi";
import { CompactTextInput } from "newui/components/ui/InputWithLabel/CompactTextInput";
import { CompactTextAreaInput } from "newui/components/ui/InputWithLabel/CompactTextAreaInput";
import { CompactSelect } from "newui/components/ui/InputWithLabel/CompactSelect";
import { EVALUATION_TYPE_OPTIONS } from "../../../../types/criteria.type";
import type { CriterionFormValues } from "./criteria-form.types";

interface CriterionFormCellsProps {
	/** Auto-focus the name input (used for the add row) */
	autoFocusName?: boolean;
	formValues: CriterionFormValues;
	isSaving: boolean;
	onSetFormValues: React.Dispatch<React.SetStateAction<CriterionFormValues>>;
	/** data-testid prefix — "add" or "edit" */
	testIdPrefix: "add" | "edit";
	onCancel: () => void;
	onSave: () => void;
	onUpdateField: (field: keyof CriterionFormValues, value: string) => void;
}

export const CriterionFormCells: React.FC<CriterionFormCellsProps> = ({
	formValues,
	isSaving,
	onUpdateField,
	onSetFormValues,
	onSave,
	onCancel,
	testIdPrefix,
	autoFocusName = false,
}) => (
	<>
		<td className="py-2 pr-4 align-top">
			<CompactTextInput
				type="text"
				value={formValues.name}
				onChange={(e) => onUpdateField("name", e.target.value)}
				placeholder={autoFocusName ? "e.g. Technical Capability" : undefined}
				className="w-full"
				autoFocus={autoFocusName}
				data-testid={`${testIdPrefix}-name-input`}
			/>
		</td>
		<td className="py-2 pr-4 align-top">
			<CompactTextAreaInput
				value={formValues.description}
				onChange={(e) => onUpdateField("description", e.target.value)}
				placeholder={autoFocusName ? "Optional description" : undefined}
				rows={1}
				className="w-full"
				data-testid={`${testIdPrefix}-description-input`}
			/>
		</td>
		<td className="py-2 pr-4 align-top">
			<CompactSelect
				tabIndex={0}
				value={formValues.rfx_evaluation_type_id}
				onChange={(e) =>
					onSetFormValues((prev) => ({
						...prev,
						rfx_evaluation_type_id: Number(e.target.value),
					}))
				}
				className="w-full"
				options={EVALUATION_TYPE_OPTIONS.map((opt) => ({
					value: opt.value,
					label: opt.label,
				}))}
				data-testid={`${testIdPrefix}-type-select`}
			/>
		</td>
		<td className="py-2 pr-4 text-right align-top">
			<CompactTextInput
				type="number"
				min="0"
				max="100"
				value={formValues.weight}
				onChange={(e) => onUpdateField("weight", e.target.value)}
				placeholder={autoFocusName ? "0" : undefined}
				className="w-20 text-right"
				inputClassName="text-right"
				data-testid={`${testIdPrefix}-weight-input`}
			/>
		</td>
		<td className="py-2 text-right">
			<div className="flex items-center justify-end gap-1">
				<button
					onClick={onSave}
					disabled={isSaving}
					className="rounded p-1 text-green-600 hover:bg-green-50 disabled:opacity-50"
					data-testid={`save-${testIdPrefix === "add" ? "new" : "edit"}-btn`}
				>
					{isSaving ? <Spinner size="xs" /> : <FiCheck size={16} />}
				</button>
				<button
					onClick={onCancel}
					className="rounded p-1 text-gray-400 hover:bg-gray-100"
					data-testid={`cancel-${testIdPrefix === "add" ? "new" : "edit"}-btn`}
				>
					<FiX size={16} />
				</button>
			</div>
		</td>
	</>
);
